import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell } from 'react-icons/fi';
import notificationService from '../../services/notificationService';
import useAuth from '../../hooks/useAuth';
import { formatDateTime } from '../../utils/formatters';
import './NotificationBell.css';

const POLL_MS = 60000;

// In-app notifications: a bell with an unread count and a short list.
const NotificationBell = () => {
  const navigate = useNavigate();
  const { mfaStatus } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapperRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const result = await notificationService.list();
      setItems(result.data);
      setUnread(result.unread_count);
    } catch (e) {
      // The bell is a convenience: stay quiet if it cannot load.
    }
  }, []);

  // Until two-factor sign-in is finished the server refuses everything, so load again as soon as it is ok.
  useEffect(() => {
    if (mfaStatus !== 'ok') return undefined;
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh, mfaStatus]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = async (item) => {
    if (!item.read_at) {
      await notificationService.markRead(item.id).catch(() => {});
      refresh();
    }
    setOpen(false);
    // Only follow links that point back into this app.
    if (item.action_url) {
      try {
        const url = new URL(item.action_url, window.location.origin);
        if (url.origin === window.location.origin) navigate(url.pathname + url.search);
      } catch (e) {
        // ignore malformed links
      }
    }
  };

  const markAll = async () => {
    await notificationService.markAllRead().catch(() => {});
    refresh();
  };

  return (
    <div className="notif" ref={wrapperRef}>
      <button
        type="button"
        className="notif__button"
        onClick={() => {
          if (!open) refresh();
          setOpen((o) => !o);
        }}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <FiBell aria-hidden="true" />
        {unread > 0 && <span className="notif__badge">{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div className="notif__panel" role="dialog" aria-label="Notifications">
          <div className="notif__head">
            <strong>Notifications</strong>
            {unread > 0 && (
              <button type="button" className="notif__link" onClick={markAll}>Mark all read</button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="notif__empty">Nothing yet.</p>
          ) : (
            <ul className="notif__list">
              {items.map((n) => (
                <li key={n.id}>
                  <button type="button" className={`notif__item ${n.read_at ? '' : 'is-unread'}`} onClick={() => openItem(n)}>
                    <span className="notif__title">{n.title}</span>
                    <span className="notif__body">{n.body}</span>
                    <span className="notif__time">{formatDateTime(n.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
